// Slice 2L · Levi Schedule Awareness — deterministic derivation.
//
// Pure, side-effect-free function that turns the read-only
// GET /api/schedule-events/levi-context payload into a small list of
// contractor-facing observations Levi is safe to surface.
//
// Contract (never break):
//   * Input is READ-ONLY; this module never mutates it.
//   * Return value is always an array (possibly empty).
//   * Each observation shape: { key, tone, text, action? }
//         key   — stable id (also used as data-testid + dedup key)
//         tone  — "info" | "warning" | "success"
//         text  — string derived from t(…) so EN/ES stays honest
//         action — optional { label, to } — always a react-router
//                  navigation. Levi never triggers a mutation.
//
// Product principle: "Levi recommends. The contractor decides."
// Levi only reads recorded schedule facts — no invented dates, no
// silent scheduling, no customer messaging.

const _num = (n) => (typeof n === "number" && Number.isFinite(n) ? n : 0);

/**
 * Derive Levi schedule observations.
 *
 * @param {object} ctx - Payload from GET /api/schedule-events/levi-context.
 * @param {(path: string, ...args: any[]) => string} t - Translator.
 * @param {object} [opts]
 * @param {number} [opts.maxProjectRollups=3] - How many upcoming projects to surface.
 * @param {string} [opts.lang="en"] - Locale used to format next-visit dates.
 * @returns {Array<{key:string,tone:string,text:string,action?:object}>}
 */
export function deriveScheduleObservations(ctx, t, opts = {}) {
  const out = [];
  if (!ctx || typeof ctx !== "object") return out;
  const counts = ctx.counts || {};
  const maxProjectRollups = Math.max(1, Number(opts.maxProjectRollups) || 3);
  const lang = opts.lang || "en";

  // Global empty state — nothing scheduled at all.
  const totalKnown =
    _num(counts.today) +
    _num(counts.this_week) +
    _num(counts.in_progress) +
    _num(counts.overdue_in_progress) +
    ((ctx.by_project || []).length);

  if (totalKnown === 0) {
    out.push({
      key: "schedule_empty",
      tone: "info",
      text: t("schedule.company.levi.empty_state"),
    });
    return out;
  }

  // 1. Today's workload.
  if (_num(counts.today) > 0) {
    out.push({
      key: "today_workload",
      tone: "info",
      text: t("schedule.company.levi.today_count", _num(counts.today)),
    });
  } else {
    out.push({
      key: "today_empty",
      tone: "info",
      text: t("schedule.company.levi.today_empty"),
    });
  }

  // 2. This-week workload.
  if (_num(counts.this_week) > 0) {
    out.push({
      key: "this_week_workload",
      tone: "info",
      text: t("schedule.company.levi.this_week_count", _num(counts.this_week)),
    });
  }

  // 3. Conflicts.
  if (_num(counts.conflict_pairs) > 0) {
    out.push({
      key: "conflict_pairs",
      tone: "warning",
      text: t("schedule.company.levi.conflict_pairs", _num(counts.conflict_pairs)),
    });
  }

  // 4. Overdue in-progress work.
  if (_num(counts.overdue_in_progress) > 0) {
    out.push({
      key: "overdue_in_progress",
      tone: "warning",
      text: t("schedule.company.levi.overdue_in_progress", _num(counts.overdue_in_progress)),
    });
  }

  // 5. Per-project rollup — Levi surfaces the next few upcoming jobs
  // (customer + next visit date) so the contractor can quickly answer
  // "when are we installing on Main Street?" from recorded facts.
  const rows = Array.isArray(ctx.by_project) ? ctx.by_project : [];
  const upcoming = rows.filter((r) => r && r.next_start_at).slice(0, maxProjectRollups);
  for (const r of upcoming) {
    if (!r.project_id) continue;
    out.push({
      key: `next_visit__${r.project_id}`,
      tone: "info",
      text: t(
        "schedule.company.levi.next_visit_row",
        (r.customer_name || "").trim() || t("schedule.company.customer_untitled"),
        formatScheduleDate(r.next_start_at, lang),
      ),
      action: {
        label: t("schedule.company.open_project"),
        to: `/app/projects/${r.project_id}`,
      },
    });
  }

  return out;
}

/**
 * Format an ISO date string as a locale-aware short date used inside
 * Levi observations. Never throws.
 */
export function formatScheduleDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    }).format(new Date(iso));
  } catch (_) {
    return iso;
  }
}
